import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import FocusedDialog from '../ui/FocusedDialog';
import Button from '../ui/Button';
import { X, EyeOff, AlertTriangle, PauseCircle, RefreshCw } from 'lucide-react';
import { MarketplaceListing } from '../../types/marketplace';
import marketplaceService from '../../services/marketplaceService';

interface UnpublishBasketDialogProps {
  listing: MarketplaceListing;
  onClose: () => void;
  onSuccess: () => void;
}

interface OpenPositionsCount {
  total: number;
  by_status: { open: number; entry_pending: number; exit_pending: number };
  affected_user_count: number;
  strategy_count: number;
}

/**
 * Soft-unpublish confirmation. The backend sets `is_published=false`; existing
 * subscriber allocations stay ACTIVE and the trading engine keeps firing for
 * them. New subscriptions are blocked. The dialog spells out the semantics so
 * an admin doesn't expect existing positions to be force-closed.
 *
 * Wind-down assistant: shows live open-position count and offers a one-click
 * "Pause strategies" action that stops new entries (publisher + every
 * subscriber) while letting existing positions exit naturally. The intended
 * sequence is: pause strategies → wait for positions to drain → unpublish.
 */
const UnpublishBasketDialog: React.FC<UnpublishBasketDialogProps> = ({ listing, onClose, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openPositions, setOpenPositions] = useState<OpenPositionsCount | null>(null);
  const [positionsLoading, setPositionsLoading] = useState(true);
  const [positionsError, setPositionsError] = useState<string | null>(null);
  const [pauseInFlight, setPauseInFlight] = useState(false);
  const [pauseResult, setPauseResult] = useState<{ updated: number; total: number } | null>(null);
  const subscriberCount = listing.subscriber_count || 0;

  // Auto-fetch open-position count on dialog open. Re-fetched after a pause
  // action so the user sees positions draining as exits fire.
  const fetchOpenPositions = async () => {
    setPositionsLoading(true);
    setPositionsError(null);
    try {
      const data = await marketplaceService.getListingOpenPositions(listing.basket_id);
      setOpenPositions(data);
    } catch (e: any) {
      setPositionsError(e.message || 'Failed to load open positions');
    } finally {
      setPositionsLoading(false);
    }
  };
  useEffect(() => {
    fetchOpenPositions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing.basket_id]);

  const handlePauseStrategies = async () => {
    if (pauseInFlight) return;
    setPauseInFlight(true);
    setError(null);
    try {
      const result = await marketplaceService.pauseAllStrategiesInBasket(listing.basket_id);
      setPauseResult({ updated: result.updated_count, total: result.total });
      // No need to re-fetch positions immediately — pausing only stops NEW
      // entries; the open-position count won't change until exits fire on
      // schedule. We still re-fetch so a long-running dialog stays current.
      fetchOpenPositions();
    } catch (e: any) {
      setError(e.message || 'Failed to pause strategies');
    } finally {
      setPauseInFlight(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  const handleConfirm = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      await marketplaceService.unpublishBasket(listing.basket_id);
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to unpublish basket');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <FocusedDialog
        ariaLabel="Unpublish marketplace listing"
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto"
      >
        <Card className="border-0 shadow-none">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <EyeOff className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                Unpublish "{listing.title}"?
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                disabled={isSubmitting}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>

          <CardContent>
            <div className="space-y-4">
              {/* Subscriber + open-position summary */}
              {subscriberCount > 0 ? (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-amber-900 dark:text-amber-200 space-y-1">
                      <div>
                        <span className="font-semibold">{subscriberCount}</span>{' '}
                        {subscriberCount === 1 ? 'subscriber is' : 'subscribers are'} currently using this basket.
                      </div>
                      <div className="text-xs">
                        {positionsLoading ? (
                          <span className="inline-flex items-center gap-1">
                            <RefreshCw className="h-3 w-3 animate-spin" /> Checking live positions…
                          </span>
                        ) : positionsError ? (
                          <span className="text-red-700 dark:text-red-300">
                            Couldn't load open positions: {positionsError}
                          </span>
                        ) : openPositions ? (
                          <>
                            <span className="font-semibold">{openPositions.total}</span> open{' '}
                            {openPositions.total === 1 ? 'position' : 'positions'} across{' '}
                            <span className="font-semibold">{openPositions.affected_user_count}</span>{' '}
                            {openPositions.affected_user_count === 1 ? 'user' : 'users'}
                            {openPositions.total > 0 && (
                              <>
                                {' '}
                                ({openPositions.by_status.open} open · {openPositions.by_status.entry_pending} entry-pending · {openPositions.by_status.exit_pending} exit-pending)
                              </>
                            )}
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-600 rounded-lg p-3 text-sm text-gray-700 dark:text-gray-300">
                  No active subscribers — safe to unpublish.
                </div>
              )}

              {/* Wind-down recommendation: pause strategies first. Gate
                  is "any open positions exist" — covers both subscriber
                  positions AND orphan publisher-only positions (F6). */}
              {openPositions && openPositions.total > 0 && !pauseResult && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 space-y-2">
                  <div className="text-sm text-blue-900 dark:text-blue-200">
                    <span className="font-semibold">Recommended:</span> pause strategies first so no new entries fire while existing positions exit naturally. Then unpublish once positions drain.
                  </div>
                  <Button
                    type="button"
                    onClick={handlePauseStrategies}
                    disabled={pauseInFlight || isSubmitting}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    leftIcon={pauseInFlight ? undefined : <PauseCircle className="h-4 w-4" />}
                  >
                    {pauseInFlight ? 'Pausing strategies…' : 'Pause all strategies'}
                  </Button>
                </div>
              )}

              {/* Pause result */}
              {pauseResult && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-sm text-green-800 dark:text-green-200">
                  Paused <span className="font-semibold">{pauseResult.updated}</span> of{' '}
                  <span className="font-semibold">{pauseResult.total}</span> strategies.
                  New entries are stopped for the publisher and every subscriber.
                  Exits continue firing — open positions will drain on schedule.
                </div>
              )}

              {/* Semantics */}
              <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                <p className="font-medium text-gray-900 dark:text-white">What will happen:</p>
                <ul className="space-y-1.5 list-disc list-inside marker:text-gray-400">
                  <li>The listing disappears from the public marketplace.</li>
                  <li>No new subscriptions can be created (self-serve or admin-on-behalf).</li>
                  <li>
                    Existing subscribers keep their allocations active —{' '}
                    <span className="font-medium">the trading engine continues to fire</span>{' '}
                    entries, exits, stop-losses, and target-profits for them.
                  </li>
                  <li>Open positions exit naturally per their schedule, SL, or TP.</li>
                  <li>Subscribers can cancel manually from their Subscriptions page anytime.</li>
                  <li>You can re-publish later — listing data (title, backtest, groups) is preserved.</li>
                </ul>
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-800 dark:text-red-200">
                  {error}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleConfirm}
                  disabled={isSubmitting}
                  className="flex-1 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 dark:disabled:bg-amber-800 text-white"
                  leftIcon={isSubmitting ? undefined : <EyeOff className="h-4 w-4" />}
                >
                  {isSubmitting ? 'Unpublishing…' : 'Unpublish'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </FocusedDialog>
    </div>
  );
};

export default UnpublishBasketDialog;
